class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    const item = parsedState.items?.[0] || parsedState;
    this.cartItemKey = item.key;
    const updates = this.getSectionsToRender().map((section) => ({
      target: document.getElementById(section.id),
      html: section.id === 'cart-notification-product'
        ? this.getProductInnerHTML(parsedState.sections?.[section.id], item)
        : this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector),
    }));

    // The add already succeeded. A missing/ambiguous rendered section must not
    // trigger another addition or show a different existing cart line.
    if (updates.some((update) => !update.target || update.html === null)) {
      window.location.assign(routes.cart_url);
      return;
    }
    updates.forEach((update) => {
      update.target.innerHTML = update.html;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getProductInnerHTML(html, item) {
    if (typeof html !== 'string') return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const exact = item.key && doc.getElementById(`cart-notification-product-${item.key}`);
    if (exact) return exact.innerHTML;

    // Shopify can change a line's key when an automatic discount is applied.
    // Match its variant only when the server rendered exactly one such line.
    const variantId = String(item.variant_id || item.id || '');
    if (!/^\d+$/.test(variantId)) return null;
    const prefix = `cart-notification-product-${variantId}:`;
    const matches = [...doc.querySelectorAll('[id^="cart-notification-product-"]')]
      .filter((node) => node.id.startsWith(prefix));
    return matches.length === 1 ? matches[0].innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
